import React from "react";
import { CheckCircleIcon } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import { BASIC, COMPREHENSIVE, SIGNATURE } from "@/constants";

export type ComparisonCoverType =
  | typeof BASIC
  | typeof COMPREHENSIVE
  | typeof SIGNATURE;

export type ComparisonCardData = {
  coverType: ComparisonCoverType;
  coverName: string;
  totalPrice: string;
  perMonthPrice: string;
  bullets: string[];
};

type Props = {
  data: ComparisonCardData;
  isSelected: boolean;
  isMobile: boolean;
  selectedLabel: string;
  viewLabel: string;
  orSeparator: string;
  monthlySuffix: string;
  onClick: () => void;
};

const headerBgClass: Record<ComparisonCoverType, string> = {
  BASIC: "bg-dark-surface-accent-2-primary",
  COMPREHENSIVE: "bg-dark-surface-accent-3-primary",
  SIGNATURE: "bg-text-body",
};

const selectedBorderColor: Record<ComparisonCoverType, string> = {
  BASIC: "border-dark-surface-accent-2-primary",
  COMPREHENSIVE: "border-dark-surface-accent-3-primary",
  SIGNATURE: "border-text-body",
};

const HEADER_TEXT_CLASS = "text-dark-text-title";

function CoverLevelComparisonCard({
  data,
  isSelected,
  isMobile,
  selectedLabel,
  viewLabel,
  orSeparator,
  monthlySuffix,
  onClick,
}: Readonly<Props>) {
  const { coverType, coverName, totalPrice, perMonthPrice, bullets } = data;

  const [suffixFirstWord, ...suffixRestWords] = (monthlySuffix ?? "").split(
    " "
  );
  const suffixRest = suffixRestWords.join(" ");

  let cardBorderClass = "";

  if (!isMobile) {
    cardBorderClass = isSelected
      ? `rounded-b-2xl border-b-4 border-l-4 border-r-4 ${selectedBorderColor[coverType]}`
      : "rounded-b-2xl border-b border-l border-r border-border-default";
  }

  return (
    <div
      className={`flex flex-col  h-full px-spacing-xxs py-spacing-xs md:p-0  overflow-hidden ${headerBgClass[coverType]} md:bg-surface-neutral-option-1`}
    >
      <div
        className={`${headerBgClass[coverType]} ${
          isMobile ? "" : "p-spacing-s rounded-t-2xl min-h-[116px]"
        }`}
      >
        <p
          className={`font-header-font ${
            isMobile
              ? "text-[14px] leading-[21px] pb-spacing-xs"
              : "text-[23px] leading-[28px]"
          } ${HEADER_TEXT_CLASS}`}
        >
          {coverName}
        </p>

        <p className={`font-body-font md:pt-spacing-xs ${HEADER_TEXT_CLASS}`}>
          <span className="font-[700] text-[18px] md:text-[20px] leading-custom-size-xxl md:leading-[23px]">
            {totalPrice}
          </span>
          {totalPrice && perMonthPrice && (
            <span
              className={`block text-left md:inline md:text-inherit font-body-font font-[600] md:ml-spacing-xs text-2xs leading-[24px] ${HEADER_TEXT_CLASS}`}
            >
              {orSeparator}
            </span>
          )}
        </p>
        {perMonthPrice && (
          <p className={`font-body-font md:mt-spacing-xs ${HEADER_TEXT_CLASS}`}>
            <span className="font-[700] text-[18px] md:text-[20px] leading-custom-size-xxl md:leading-[23px]">{`${perMonthPrice} `}</span>
            <span
              className={`font-[600] text-2xs leading-[24px] ${HEADER_TEXT_CLASS}`}
            >
              {suffixRest ? `${suffixFirstWord} ` : suffixFirstWord}
            </span>
            {suffixRest && (
              <span
                className={`block text-left md:inline font-[600] text-2xs leading-[24px] ${HEADER_TEXT_CLASS}`}
              >
                {suffixRest}
              </span>
            )}
          </p>
        )}
      </div>
      <div
        className={`flex h-full flex-col max-md:justify-end md:justify-between md:py-spacing-m box-border md:gap-spacing-m ${cardBorderClass}`}
      >
        {!isMobile && (
          <ul className="flex flex-col gap-spacing-xs list-disc [&>li]:marker:text-4xs pl-spacing-xxs pr-spacing-xs ml-spacing-l">
            {bullets.map((bullet, index) => (
              <li
                key={index as unknown as string}
                className="font-body-font text-text-functional-1 text-2xs font-[400] leading-[24px]"
              >
                {bullet}
              </li>
            ))}
          </ul>
        )}
        <div className={isMobile ? "pt-spacing-xs" : "px-spacing-s"}>
          {isSelected && isMobile && (
            <LabelButtonWrapper
              label={selectedLabel}
              hierarchy="primary"
              className="!h-[40px] !text-xs !rounded-radius-full !bg-[#ffd050] !border-[#ffd050] hover:!bg-[#ffd973] hover:!border-[#ffd973]"
              onClick={onClick}
            />
          )}
          {isSelected && !isMobile && (
            <LabelButtonWrapper
              label={selectedLabel}
              hierarchy="primary"
              isRightIcon
              rightIcon={
                <span className="ml-spacing-xxs flex items-center">
                  <CheckCircleIcon
                    fill="rgba(39, 36, 39, 1)"
                    stroke="#ffd050"
                  />
                </span>
              }
              className="!h-[40px] !text-xs !rounded-radius-full !bg-[#ffd050] !border-[#ffd050] hover:!bg-[#ffd973] hover:!border-[#ffd973]"
              onClick={onClick}
            />
          )}
          {!isSelected && (
            <LabelButtonWrapper
              label={viewLabel}
              hierarchy="secondary"
              className="!h-[40px] !text-xs !rounded-radius-full !bg-white !text-[#136a9c] !border-[#136a9c] max-md:!border-0 hover:!bg-[#e6e6e6]"
              onClick={onClick}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default CoverLevelComparisonCard;
